import { useAuth } from '@auth/useAuth'
import AppPaths from '@globals/paths'
import { TENANT_SESSION_REGEX } from '@globals/stores/useAuthStore'
import { hasFirstLoginParam } from '@guest/login/desklessSignup/firstLoginQueryParam'
import { preserveQueryParams } from '@shared/utils/queryParams'
import { API, initiateTenantLogin, listTenants, Token } from '@kudos/http-client'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import { isMfaRequiredForTenant } from '@shared/errors/authErrorUtils'
import { getDomain } from '@shared/utils/getDomain'
import { T } from '@transifex/react'
import { isAxiosError } from 'axios'
import { useCallback, useEffect, useRef } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'

/**
 * Exchanges a user-scope session for a tenant session, then redirects into the app.
 *
 * Arrives here from password login (and similar flows) with `?session=…` on the URL.
 * Optional query params:
 * - `next` — deep link to open after tenant login
 * - `firstLogin` — deskless post-signup flag; sends the user to add-recovery-method instead of home
 * - `userEmail` — forwarded to the tenant picker when multiple tenants exist
 */
export const TenantLoginRoute = () => {
  const [searchParams] = useSearchParams()
  const { initialize, isTenantScope } = useAuth()

  const session = searchParams.get('session')
  const next = searchParams.get('next')
  const userEmail = searchParams.get('userEmail')

  // Step 1: Must have a session to proceed.
  if (!session) return <Navigate to={AppPaths.anon.userLogin()} />

  // Step 2: Already in tenant scope — route by intent (firstLogin beats next beats home).
  if (isTenantScope && hasFirstLoginParam(searchParams)) {
    return <Navigate to={AppPaths.tenant.addDesklessRecoveryMethod()} />
  }

  if (isTenantScope && !!next) {
    return <Navigate to={next} />
  }

  if (isTenantScope) return <Navigate to={AppPaths.tenant.home()} />

  // Step 3: User-scope session only — exchange for tenant session, then re-render into step 2.
  return (
    <InitializeSession
      key={session}
      session={session}
      onSuccess={initialize}
      userEmail={userEmail}
      loginSearchParams={searchParams}
    />
  )
}

type InitializeSessionProps = {
  session: string
  onSuccess: (token: Token, options?: { persist?: boolean; skipMe?: boolean }) => void
  userEmail: string | null
  /** Full query string from {@link TenantLoginRoute} (carries `firstLogin`, `next`, etc.). */
  loginSearchParams: URLSearchParams
}

/** Resolves a user-scope session into a tenant session (or sends the user to tenant selection). */
const InitializeSession = ({
  session,
  onSuccess,
  userEmail,
  loginSearchParams,
}: InitializeSessionProps) => {
  const hasInitialized = useRef<boolean>(false)
  const retryCount = useRef<number>(0)
  const navigate = useNavigate()
  const domain = getDomain()

  const buildTenantsPickerSearch = useCallback(() => {
    const params = preserveQueryParams(loginSearchParams, ['session'])
    if (userEmail) params.set('userEmail', userEmail)
    return params.toString()
  }, [loginSearchParams, userEmail])

  const initializeWithRetry = useCallback(async () => {
    try {
      API.defaults.headers.common['Authorization'] = session
      // If the session looks tenant-scoped (session-<user>-<tenant>), initialize immediately (SAML path)
      const isTenantSession = TENANT_SESSION_REGEX.test(session)
      if (isTenantSession) {
        onSuccess({ session } as Token)
        return
      }

      const tenants = await listTenants(domain)
      if (!tenants || tenants.length === 0) throw new Error('No accessible tenants')
      if (tenants.length > 1) {
        // Persist user-scope session until the tenant is chosen; skipMe to avoid MissingMemberContext
        onSuccess({ session } as Token, {
          persist: true,
          skipMe: true,
        })
        // Carry firstLogin, next, etc. onto the tenant picker (session stays in auth state).
        navigate({
          pathname: AppPaths.user.tenants(),
          search: buildTenantsPickerSearch(),
        })
        return
      }
      // Single tenant — log in directly and let TenantLoginRoute step 2 handle redirect.
      const tenantId = tenants[0].id
      const { session: tenantSession } = await initiateTenantLogin({ tenantId })
      onSuccess({ session: tenantSession } as Token)
    } catch (error: unknown) {
      // Only redirect to login for authentication errors (401, 403) or tenant access errors
      if (isAxiosError(error)) {
        // If MFA is required for the tenant, persist the user-scope session and
        // navigate to the tenants page where the MFA setup dialog will be shown.
        if (isMfaRequiredForTenant(error)) {
          onSuccess({ session } as Token, {
            persist: true,
            skipMe: true,
          })
          navigate({
            pathname: AppPaths.user.tenants(),
            search: buildTenantsPickerSearch(),
          })
          return
        }

        const status = error.response?.status
        if (status === 401 || status === 403 || status === 404) {
          // Authentication failed or tenant not found - redirect to login
          window.location.replace(AppPaths.anon.userLogin())
          return
        }

        // For server errors (500, 502, 503), try to retry a few times before giving up
        if (status && status >= 500 && retryCount.current < 3) {
          retryCount.current++
          // Wait a bit before retrying (exponential backoff)
          setTimeout(() => initializeWithRetry(), Math.pow(2, retryCount.current) * 1000)
          return
        }
      }

      // For persistent errors or non-HTTP errors, redirect to login
      window.location.replace(AppPaths.anon.userLogin())
    }
  }, [session, domain, navigate, onSuccess, buildTenantsPickerSearch])

  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true
    initializeWithRetry()
  }, [session, initializeWithRetry])

  return (
    <>
      <Typography>
        <T _str="Logging into tenant..." />
      </Typography>
      <LinearProgress />
    </>
  )
}

export default TenantLoginRoute
